// LocationForm.jsx

import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkedAlt } from 'react-icons/fa';
import { useAdminData } from '../../context/SessionContext';

function LocationForm({ action, location, onSuccess, activeUser, isRequest = false }) {
    const [city, setCity] = useState('');
    const [county, setCounty] = useState(''); // ← always store ID here
    const [address, setAddress] = useState('');
    const [lat, setLat] = useState(null);
    const [lng, setLng] = useState(null);    

    const { counties } = useAdminData();

    const addressInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const lastEmittedRef = useRef({});

    const hasLocationChanged = (a, b) => {
        return a.address !== b.address ||
            a.city !== b.city ||
            a.county !== b.county;
    };

    useEffect(() => {
        if ((action === 1 && location) || (isRequest && location)) {
            const incoming = {
                address: location.address || '',
                city: location.city || '',
                county: location.county?.id ?? location.county ?? '',
                lat: location.lat ?? null,
                lng: location.lng ?? null,
            };

            if (hasLocationChanged(lastEmittedRef.current, incoming)) {
                setAddress(incoming.address);
                setCity(incoming.city);
                setCounty(incoming.county); // ← now always an ID
                setLat(incoming.lat);
                setLng(incoming.lng);
                lastEmittedRef.current = incoming;
            }
        }
    }, [action, isRequest, location]);

    useEffect(() => {
        const current = { address, city, county, lat, lng };
        const last = lastEmittedRef.current;

        if (hasLocationChanged(last, current)) {
            lastEmittedRef.current = { address, city, county };
            onSuccess(current);
        }
    }, [address, city, county, lat, lng, onSuccess]);

    // Google Autocomplete (unchanged)
    useEffect(() => {
        if (!window.google?.maps?.places) return;

        if (addressInputRef.current && !autocompleteRef.current) {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                addressInputRef.current,
                {
                    componentRestrictions: { country: 'KE' },
                    fields: ['address_components', 'geometry', 'formatted_address'],
                }
            );

            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                if (!place.geometry) return;

                const fullAddress = place.formatted_address || '';
                let detectedCity = '';

                if (place.address_components) {
                    const cityComp = place.address_components.find(c =>
                        c.types.includes('locality')
                    );
                    detectedCity = cityComp?.long_name || '';
                }

                setAddress(fullAddress);
                setCity(detectedCity);
                // Keep existing county (as ID)

                setLat(place.geometry.location.lat());
                setLng(place.geometry.location.lng());

                const payload = {
                    address: fullAddress,
                    city: detectedCity,
                    county: county, // ← still an ID
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };
                lastEmittedRef.current = { address: fullAddress, city: detectedCity, county: county };
                onSuccess(payload);
            });
        }

        return () => {
            if (autocompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="pb-7">
            <div className='flex justify-between'>
                <h4 className="text-lg font-bold">Location Info:</h4>
                {(action === 1) && (activeUser?.location?.lat && activeUser?.location?.lng) ? (
                    <div>
                        <div
                            onClick={() => {
                                document.getElementById('location-open-btn').click();
                            }}
                            className='btn btn-accent btn-sm text-white'
                        >
                            <FaMapMarkedAlt /> Open Map
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="form-control mb-3">
                <label className="label"><span className="label-text">Full Address *</span></label>
                <input
                    ref={addressInputRef}
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="e.g. 123 Mama Ngina Street, Nairobi"
                    className="input input-bordered w-full"
                    required
                />
            </div>

            <div className="flex flex-col md:flex-row gap-5 mb-3">
                <div className="form-control w-full">
                    <label className="label"><span className="label-text">City/Town</span></label>
                    <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="e.g. Nairobi"
                        className="input input-bordered w-full"
                    />
                </div>
                <div className="form-control w-full">
                    <label className="label"><span className="label-text">County</span></label>
                    <select
                        required
                        value={county} // ← now always matches option value (ID)
                        onChange={(e) => setCounty(e.target.value)}
                        className="select select-bordered"
                    >
                        <option value="">Select County</option>
                        {counties?.map((pt) => (
                            <option key={pt.id} value={pt.id}> {/* 👈 value is ID */}
                                {pt.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

export default LocationForm;